import { Injectable } from "@angular/core";

import { GiviScanSessionModel, SessionModel } from '@models/SessionModel';
import { LoginModel } from '@models/LoginModel';
import { CustomerModel } from '@models/CustomerModel';
import { VendorStoreCustomer } from '@shared/models/VendorModel';

import { environment } from "@environments/environment";
import { TransactionModel } from "@shared/models/TransactionModel";

@Injectable()
export class SessionManager {
    // Singleton
    static instance: SessionManager;

    private static SESSION_KEY = environment.production
        ? "GIVI_ADMIN_SESSION"
        : "DEMO_GIVI_ADMIN_SESSION";
    private static LOGIN_CRED_KEY = environment.production
        ? "GIVI_ADMIN_LOGIN_CRED_KEY"
        : "DEMO_GIVI_ADMIN_LOGIN_CRED_KEY";

    // SCAN
    private static SCANLOGIN_CRED_KEY = environment.production
        ? 'GIVI_SCANLOGIN_CRED_KEY'
        : 'DEMO_GIVI_SCANLOGIN_CRED_KEY';

    // TEST
    private static TEST_SESSION_KEY = "TEST_GIVI_ADMIN_SESSION";
    private static TEST_LOGIN_CRED_KEY = "TEST_GIVI_ADMIN_LOGIN_CRED_KEY";
    private static TEST_SCANLOGIN_CRED_KEY = 'TEST_GIVI_SCANLOGIN_CRED_KEY';

    private static session: SessionModel;

    private static credentials: LoginModel;
    private static giviScanCredentials: LoginModel;

    private static mobiloudApp: any;

    private static givis: TransactionModel[] = [];
    private static recentPurchases: TransactionModel[] = [];

    private static demoMode = false;

    constructor() {
        return (SessionManager.instance = SessionManager.instance || this);
    }

    // Credentials

    // Gets current credentials
    public static getCredentials(): LoginModel {
        if (!this.credentials) {
            const objStr = localStorage.getItem(this._getLoginCredKey());
            if (objStr) {
                try {
                    const credentials = JSON.parse(objStr);
                    this.credentials = new LoginModel(credentials);
                } catch (error) {
                    this.clearCredentials();
                }
            }
        }
        return this.credentials;
    }

    // Gets current credentials
    public static getGiviScanCredentials(): LoginModel {

        if (!this.giviScanCredentials) {

            const objStr = localStorage.getItem(this._getScanLoginCredKey());
            if (objStr) {

                try {
                    const credentials = JSON.parse(objStr);
                    this.giviScanCredentials = new LoginModel(credentials);
                } catch (error) {
                    this.clearGiviScanSession();
                }
            }
        }
        return this.giviScanCredentials;
    }

    // Sets credentials and returns it
    public static setCredentials(credentials: LoginModel): LoginModel {
        this.credentials = credentials;
        this.setLocalStorageCredentials();

        return this.credentials;
    }

    // Sets credentials and returns it
    public static setGiviScanCredentials(giviScanCredentials: LoginModel): LoginModel {

        this.giviScanCredentials = giviScanCredentials;
        this.setLocalStorageCredentials();

        return this.giviScanCredentials;
    }

    // Updates the remember in credentials
    public static updateCredentialsRemember(remember: boolean) {
        SessionManager.credentials.remember = remember;
        SessionManager.setLocalStorageCredentials();
    }

    // Set credentials to null and removes credentials from local storage
    public static clearCredentials() {
        SessionManager.credentials = null;
        localStorage.removeItem(this._getLoginCredKey());
        localStorage.removeItem(this._getScanLoginCredKey());
    }

    // Updates credentials in local storage
    public static setLocalStorageCredentials() {
        // store the session to be validated when required
        localStorage.setItem(
            this._getLoginCredKey(),
            JSON.stringify(this.credentials)
        );
    }

    // Session

    // Gets current session
    public static getSession(force: boolean = false): SessionModel {
        if (force || !this.session) {
            const objStr = localStorage.getItem(this._getSessionKey());
            if (objStr) {
                try {
                    const session = JSON.parse(objStr);
                    this.session = new SessionModel(session);
                    this.setSession(this.session);
                } catch (error) {
                    this.clearSession();
                }
            }
        }
        return this.session;
    }

    // Sets session and returns it
    public static setSession(session: SessionModel): SessionModel {

        this.session = session;

        this.setLocalStorageSession();
        return this.session;
    }

    // Updates the user in session
    public static updateSessionAccount(user: CustomerModel) {

        if (SessionManager.session != null) {
            SessionManager.session.user = user;
            SessionManager.setLocalStorageSession();
        }
    }

    public static updateSessionStoreGSA(store: VendorStoreCustomer) {

        if (SessionManager.session != null && SessionManager.session.giviScanSession != null) {
            SessionManager.session.giviScanSession.selectedStore = store;
            SessionManager.setLocalStorageSession();
        }
    }

    public static updateSessionStore(store: VendorStoreCustomer) {

        if (SessionManager.session != null) {
            SessionManager.session.selectedStore = store;
            SessionManager.setLocalStorageSession();
        }
    }

    // Updates the user in session
    public static updateSessionTokens(accessToken: string, refreshToken: string) {

        if (SessionManager.session != null) {
            SessionManager.session.accessToken = accessToken;
            SessionManager.session.refreshToken = refreshToken;
            SessionManager.setLocalStorageSession();
        }
    }

    // Set session to null and removes session from local storage
    public static clearSession() {

        SessionManager.session = null;
        localStorage.removeItem(this._getSessionKey());
    }

    public static clearGiviScanSession() {

        if (SessionManager.session != null) {
            SessionManager.session.giviScanSession = null;
            this.setLocalStorageSession();
        }
    }

    //set givi scan session
    public static setGiviScanSession(session: GiviScanSessionModel) {

        if (SessionManager.session != null) {
            SessionManager.session.giviScanSession = session;
            this.setLocalStorageSession();
        }
    }

    // Updates session in local storage
    public static setLocalStorageSession() {
        // store the session to be validated when required
        localStorage.setItem(this._getSessionKey(), JSON.stringify(this.session));
    }

    public static setMobiloudApp(app: any) {
        SessionManager.mobiloudApp = app;
    }

    public static getMobiloudApp(): any {
        return SessionManager.mobiloudApp;
    }


    // Sets RecentPurchases
    public static setMyAvailableGivis(givis: TransactionModel[]) {
        SessionManager.givis = givis;
    }

    public static getMyGivis(): TransactionModel[] {
        if (!SessionManager.givis) SessionManager.givis = [];
        return SessionManager.givis;
    }

    // Sets RecentPurchases
    public static setRecentPurchases(purchases: TransactionModel[]) {
        SessionManager.recentPurchases = purchases;
    }

    public static getRecentPurchases(): TransactionModel[] {
        return SessionManager.recentPurchases;
    }

    //enable/disable DEMO MODE
    public static setDemoMode(isDemoMode: boolean) {
        SessionManager.demoMode = isDemoMode;
        //this.demoModeSubject.next(isDemoMode);
    }

    public static isDemoMode(): boolean {
        return SessionManager.demoMode;
    }



    private static _getSessionKey(): string {
        return SessionManager.isDemoMode() ?
            SessionManager.TEST_SESSION_KEY :
            SessionManager.SESSION_KEY;
    }

    private static _getLoginCredKey(): string {
        return SessionManager.isDemoMode() ?
            SessionManager.TEST_LOGIN_CRED_KEY :
            SessionManager.LOGIN_CRED_KEY;
    }

    private static _getScanLoginCredKey(): string {
        return SessionManager.isDemoMode() ?
            SessionManager.TEST_SCANLOGIN_CRED_KEY :
            SessionManager.SCANLOGIN_CRED_KEY;
    }
}
